import type {
  Consignment,
  CreateConsignmentRequest,
  CreateConsignmentResponse,
  ConsignmentDetail,
  UpdateConsignmentRequest,
  TaxEstimateRequest,
  TaxEstimateResponse,
  ParsedInvoice,
} from './api/types'
import { API_BASE_URL } from '@/lib/env'

interface ApiOptions extends RequestInit {
  params?: Record<string, string>
}

async function fetchApi<T>(path: string, options: ApiOptions = {}): Promise<T> {
  const headers = new Headers(options.headers)
  headers.set('Content-Type', 'application/json')

  let url = `${API_BASE_URL}${path}`
  if (options.params) {
    const searchParams = new URLSearchParams(options.params)
    url += `?${searchParams.toString()}`
  }

  const response = await fetch(url, {
    ...options,
    headers,
    credentials: 'include',
  })

  const data = await response.json()

  if (!response.ok) {
    throw new Error(data.error?.message || 'API request failed')
  }

  return data.data
}

export interface Page<T> {
  items: T[]
  page: number
  limit: number
  total: number
  totalPages: number
}

/**
 * Like fetchApi, but keeps the pagination envelope instead of discarding it.
 *
 * fetchApi returns `data.data`, which is right for every other call and drops
 * exactly what a paginated list needs. An endpoint that has not been paginated
 * yet still answers without a `pagination` block, so this falls back to a single
 * page covering everything it returned.
 */
async function fetchPage<T>(path: string, params: Record<string, string>): Promise<Page<T>> {
  const headers = new Headers()
  headers.set('Content-Type', 'application/json')

  const response = await fetch(`${API_BASE_URL}${path}?${new URLSearchParams(params)}`, {
    headers,
    credentials: 'include',
  })
  const body = await response.json()
  if (!response.ok) {
    throw new Error(body.error?.message || 'API request failed')
  }

  const items = (body.data ?? []) as T[]
  const pagination = body.pagination
  return {
    items,
    page: pagination?.page ?? 1,
    limit: pagination?.limit ?? items.length,
    total: pagination?.total ?? items.length,
    totalPages: pagination?.totalPages ?? 1,
  }
}

export function pageParams(page: number, limit = 25) {
  return { page: String(page), limit: String(limit) }
}

export const api = {
  consignments: {
    list: () => fetchApi<Consignment[]>('/consignments'),
    listPage: (page: number, limit = 25, search = '') =>
      fetchPage<Consignment>('/consignments', {
        ...pageParams(page, limit),
        ...(search.trim() ? { search: search.trim() } : {}),
      }),
    get: (id: string) => fetchApi<ConsignmentDetail>(`/consignments/${id}`),
    create: (data: CreateConsignmentRequest) =>
      fetchApi<CreateConsignmentResponse>('/consignments', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    update: (id: string, data: UpdateConsignmentRequest) =>
      fetchApi<Consignment>(`/consignments/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    delete: (id: string) =>
      fetchApi<{ deleted: true }>(`/consignments/${id}`, {
        method: 'DELETE',
      }),
  },
  tax: {
    estimate: (data: TaxEstimateRequest) =>
      fetchApi<TaxEstimateResponse>('/tax/estimate', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
  },
  parseInvoice: (imageUrl: string) =>
    fetchApi<ParsedInvoice>('/parse-invoice', {
      method: 'POST',
      body: JSON.stringify({ image_url: imageUrl }),
    }),
}
